import {
  ArrowDownTrayIcon,
  ArrowRightStartOnRectangleIcon,
  ArrowUpTrayIcon,
  MagnifyingGlassIcon,
  PlusIcon
} from '@heroicons/react/16/solid';
import {
  Box,
  Button,
  classSet,
  EllipsisMenu,
  InputIconVariant,
  Pagination,
  useFilteredPagination
} from '@platform/ui-library';
import { FC, useEffect, useMemo, useState } from 'react';
import { Route, Routes, useSearchParams } from 'react-router-dom';
import { KeyCode } from '@app/const';
import { LoadingSpinner, InputControl } from '@app/form_components';
import {
  useCustomNavigate,
  useLocalSerializedDesigns,
  useServices,
  useUser
} from '@app/hooks';
import {
  CustomNavigate,
  Table,
  VersionSelectorControl
} from '@app/page-components';
import { Design, TimeInMs } from '@app/types';
import { formatDate, formatTimeSince } from '@app/utilities';
import { ReactComponent as Logo } from '../../assets/interactions-full-logo.svg';
import { HomeCreatePage } from './create/page';
import { HomeDownloadPage } from './download/page';
import { HomeFeaturesPage } from './features/page';
import { HomeUploadPage } from './upload/page';
import styles from './layout.module.scss';

const isIFrame = window.top !== window.self;

const itemsPerPage = 30;

const filterColumns: (keyof Design)[] = [
  'branchId',
  'branchName',
  'clientName',
  'designName',
  'version'
];
const sortColumns: (keyof Design)[] = [
  ...filterColumns,
  'lastUpdate'
];

export const HomeLayout: FC = () => {
  const searchDesignId = useSearchParams()[0].get('designId');
  const {
    globalModalsService,
    lokiService,
    userService
  } = useServices();
  const navigate = useCustomNavigate();
  const [search, setSearch] = useState('');
  const user = useUser();
  const serializedDesigns = useLocalSerializedDesigns();

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      const { code, altKey, ctrlKey } = event;
      if (code === KeyCode.d && altKey && ctrlKey) {
        event.preventDefault();
        navigate('features');
      }
    };

    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, []);

  const localDesigns = serializedDesigns?.filter(({ designId }) => {
    return !searchDesignId || designId === searchDesignId;
  });

  const filteredTableItems = useMemo(() => {
    if (!search) {
      return localDesigns;
    }
    return localDesigns?.filter((design) => {
      return filterColumns.some((column) => {
        const columnValue = design[column];
        return columnValue?.toString().toLowerCase().includes(search.toLowerCase());
      });
    });
  }, [search, localDesigns]);

  const {
    items: tableItemsRaw,
    filteredItemsCount,
    page,
    setPage,
    hasMultiplePages,
    onSort,
    sortAsc,
    sortColumn
  } = useFilteredPagination<Design>(filteredTableItems ?? [], sortColumns, 1, itemsPerPage);

  const tableItems = tableItemsRaw.map(({
    branchId,
    branchName,
    clientName,
    designName,
    lastUpdate,
    version
  }) => {
    const lastUpdateContent = (lastUpdate < (Date.now() - TimeInMs.day)) ? (
      formatDate(lastUpdate)
    ) : (
      formatTimeSince(lastUpdate)
    );

    return {
      branchId,
      branchName,
      clientName,
      designName,
      version: {
        content: (
          <VersionSelectorControl
            branchId={branchId}
            name={`version-${branchId}`}
            onChange={async (_, option) => {
              if (option) {
                await lokiService.storeSerialized(option.serialized);
              }
            }}
            showLatestVersionDot
            value={version}
          />
        ),
        raw: version
      },
      lastUpdate: {
        content: lastUpdateContent,
        raw: lastUpdate
      },
      _actions: {
        content: (
          <>
            <Button
              icon="delete"
              type="button"
              theme="outline"
              onClick={async () => {
                if (
                  await globalModalsService.openConfirmationModal({
                    children: `Are you sure you want to delete your local copy of "${designName} ${version}"?`,
                    labels: {
                      header: 'Delete confirmation',
                      cancelButton: 'Cancel',
                      continueButton: 'Delete'
                    }
                  })
                ) {
                  lokiService.removeSerialized(branchId);
                }
              }}
            />
            <Button
              icon="edit"
              type="button"
              theme="outlineHighlight"
              onClick={() => {
                let route = `/app?g=${branchId}`;
                if (searchDesignId) {
                  route += `&designId=${searchDesignId}`;
                }
                location.href = route;
              }}
            />
          </>
        ),
        raw: ''
      }
    };
  });

  if (!user || !userService.isLoggedIn) {
    return (
      <>
        <div className={styles.background} />
        <div className={classSet(['grid-layout', styles.layout])}>
          <div className={styles.header}>
            <Logo className={styles.logo} />
          </div>
        </div>
      </>
    );
  }

  let content: JSX.Element;
  if (!localDesigns) {
    // Still loading local DB.
    content = (
      <LoadingSpinner message="Loading local design versions..." />
    );
  } else if (!localDesigns.length) {
    // No items in local DB.
    content = (
      <div className={styles.emptyStateContainer}>
        <p>No local versions were found.</p>
        <div className="flex-center flex-gap">
          <Button
            icon="download"
            onClick={() => {
              navigate('download');
            }}
            type="button"
          >
            Download a design
          </Button>
          {(searchDesignId) ? null : (
            <>
              <Button
                icon="upload"
                onClick={() => {
                  navigate('upload');
                }}
                theme='outline'
                type="button"
              >
                Upload a design
              </Button>
              <Button
                icon="plus"
                onClick={() => {
                  navigate('create');
                }}
                theme='outline'
                type="button"
              >
                Create a design
              </Button>
            </>
          )}
        </div>
      </div>
    );
  } else if (!tableItems?.length) {
    // Search filtered out all items.
    content = (
      <div className={classSet(['flex-center', styles.noResults])}>No results</div>
    );
  } else {
    content = (
      <>
        <Table
          className={styles.table}
          columnsHidden={['branchId']}
          columnsHighlighted={['designName']}
          data={tableItems as any}
          keyAsId="branchId"
          onSort={onSort}
          sortAsc={sortAsc}
          sortByColumns={sortColumns}
          sortColumn={sortColumn}
          headerValues={{
            clientName: 'Client',
            designName: 'Design',
            branchName: 'Branch',
            version: 'Version',
            lastUpdate: 'Last Update'
          }}
        />
        {hasMultiplePages && (
          <Pagination
            totalItems={filteredItemsCount}
            itemsPerPage={itemsPerPage}
            initialPage={page}
            onPageChange={setPage}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className={classSet([styles.background, styles.loggedIn])} />
      <div className={classSet(['grid-layout', styles.layout])}>
        <div className={styles.header}>
          <Logo className={styles.logo} />
          {(isIFrame) ? null : (
            <Button
              icon={ArrowRightStartOnRectangleIcon}
              onClick={() => {
                userService.user = null;
              }}
              theme='outline'
              type="button"
            >
              Log Out {user.username}
            </Button>
          )}
        </div>
        <Box className={styles.versionsBox} theme="noPadding">
          <div className={styles.boxHeader}>
            <h2>Local Versions</h2>
            <InputControl
              icon={{ left: MagnifyingGlassIcon }}
              name="search"
              onChange={setSearch}
              placeholder="Search"
              value={search}
              variant={InputIconVariant.Pill}
            />
            {(searchDesignId) ? (
              <Button
                icon="plus"
                onClick={() => {
                  navigate('download');
                }}
                type="button"
              />
            ) : (
              <div className="circle-menu">
                <EllipsisMenu
                  items={[
                    {
                      action: () => {
                        navigate('download');
                      },
                      icon: ArrowDownTrayIcon,
                      label: 'Download'
                    },
                    {
                      action: () => {
                        navigate('upload');
                      },
                      icon: ArrowUpTrayIcon,
                      label: 'Upload'
                    },
                    {
                      action: () => {
                        navigate('create');
                      },
                      icon: PlusIcon,
                      label: 'Create'
                    }
                  ]}
                >
                  <Button icon='plus' theme={['round', 'fancy', 'color']} />
                </EllipsisMenu>
              </div>
            )}
          </div>
          <div className="box content overflow-visible">
            {content}
          </div>
          <Routes>
            <Route path="download" element={<HomeDownloadPage />} />
            <Route path="features" element={<HomeFeaturesPage />} />
            {(searchDesignId) ? null : (
              <>
                <Route path="create" element={<HomeCreatePage />} />
                <Route path="upload" element={<HomeUploadPage />} />
              </>
            )}
            <Route path="*" element={<CustomNavigate to="../" />} />
          </Routes>
        </Box>
      </div>
    </>
  );
};
